import { IconAlertCircle, IconAlertTriangle, IconCircleDot, IconStack2, IconX } from '@tabler/icons-react';
import {
  Text,
  IconButton,
  ScrollArea,
  WP_ICON_NAV,
  EmptyState,
  StateIllustration,
  GhostWeightLabel,
  ProximityScope,
} from '@wapharma/design-system';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useInboxList } from '../contexts/InboxListContext';
import { useProductBulkImportContextOptional } from '../contexts/ProductBulkImportContext';
import type { ImportJobStatus } from '../hooks/useCatalogImportJobInbox';
import type { InboxItem } from '../hooks/useInboxListState';
import {
  FILTER_PILL_DANGER,
  FILTER_PILL_MUTED,
  FILTER_PILL_NEUTRAL,
  FILTER_PILL_WARNING,
} from '../lib/statusPillColors';
import { formatFriendlyDate } from '../utils/friendlyDate';
import {
  CATALOG_INBOX_OWNER,
  catalogImportAckType,
} from '../utils/catalogImportInboxAck';
import { InboxBulkImportItems } from './InboxBulkImportItems';
import { EntityToneWell } from './EntityTypeMark';
import { isCatalogImportInboxType } from './inboxCatalogImport';
import styles from './InboxPopoverContent.module.css';

/** Split `REF — detail` into primary + muted secondary (drop em dash). */
function splitInboxWhyNow(whyNow: string): { primary: string; secondary: string | null } {
  const trimmed = whyNow.trim();
  const m = trimmed.match(/^(.+?)\s+[—–]\s+(.+)$/);
  if (!m) return { primary: trimmed, secondary: null };
  return { primary: m[1].trim(), secondary: m[2].trim() };
}

const CatalogImportInboxDetail = lazy(() =>
  import('./CatalogImportInboxDetail').then((m) => ({ default: m.CatalogImportInboxDetail })),
);

type SeverityFilter = 'all' | InboxItem['severity'];

const SEVERITY_RANK: Record<InboxItem['severity'], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const SEVERITY_FILTERS: Array<{
  value: SeverityFilter;
  label: string;
  icon: typeof IconStack2;
  palette: { accent: string; bg: string; border: string };
}> = [
  { value: 'all', label: 'Tous', icon: IconStack2, palette: FILTER_PILL_NEUTRAL },
  { value: 'high', label: 'Haute', icon: IconAlertTriangle, palette: FILTER_PILL_DANGER },
  { value: 'medium', label: 'Moyenne', icon: IconAlertCircle, palette: FILTER_PILL_WARNING },
  { value: 'low', label: 'Basse', icon: IconCircleDot, palette: FILTER_PILL_MUTED },
];

function isCatalogImportItem(item: InboxItem): boolean {
  return isCatalogImportInboxType(item.type);
}

function severityItemClass(severity: InboxItem['severity'], unread: boolean): string {
  const rank =
    severity === 'high'
      ? styles.severityHigh
      : severity === 'medium'
        ? styles.severityMedium
        : styles.severityLow;
  return `${styles.inboxItem} ${rank} ${unread ? styles.inboxItemUnread : styles.inboxItemRead}`;
}

export function InboxPopoverContent({
  selectedJobId,
  onSelectJobId,
  isImportJobAcked,
  ackImportJob,
}: {
  selectedJobId: string | null;
  onSelectJobId: (jobId: string | null) => void;
  isImportJobAcked: (jobId: string, status: ImportJobStatus) => boolean;
  ackImportJob: (jobId: string) => void;
}) {
  const { items, pending, dismiss, openEntity, markSeen } = useInboxList();
  const bulkImport = useProductBulkImportContextOptional();
  const inboxImportItemCount = bulkImport?.inboxImportItemCount ?? 0;
  const jobStatus = bulkImport?.importState.jobStatus ?? null;
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');

  const detailOpen = selectedJobId != null;

  const baseItems = useMemo(
    () => items.filter((i) => !(inboxImportItemCount > 0 && isCatalogImportItem(i))),
    [items, inboxImportItemCount],
  );

  const severityCounts = useMemo(() => {
    const counts = { all: baseItems.length, high: 0, medium: 0, low: 0 };
    for (const i of baseItems) {
      counts[i.severity] += 1;
    }
    return counts;
  }, [baseItems]);

  const filteredItems = useMemo(() => {
    const list =
      severityFilter === 'all'
        ? baseItems
        : baseItems.filter((i) => i.severity === severityFilter);
    return [...list].sort((a, b) => {
      const byRank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
      if (byRank !== 0) return byRank;
      if (a.seen === b.seen) return 0;
      return a.seen ? 1 : -1;
    });
  }, [baseItems, severityFilter]);

  const showImportCards = severityFilter === 'all' || severityFilter === 'medium';
  const importVisibleCount = showImportCards ? inboxImportItemCount : 0;

  const unseenCount = filteredItems.reduce((n, i) => n + (i.seen ? 0 : 1), 0);
  const totalCount = filteredItems.length + importVisibleCount;

  const ackImportJobFull = async (jobId: string, status: ImportJobStatus) => {
    ackImportJob(jobId);
    await markSeen({
      ownerRole: CATALOG_INBOX_OWNER,
      type: catalogImportAckType(status),
      entityId: jobId,
    });
  };

  const openImportDetail = (jobId: string) => {
    void ackImportJobFull(jobId, jobStatus ?? 'RUNNING');
    onSelectJobId(jobId);
  };

  const dismissImportJob = (jobId: string, status: ImportJobStatus) => {
    void ackImportJobFull(jobId, status);
    void dismiss({
      id: jobId,
      type: catalogImportAckType(status),
      entityType: 'CatalogImportJob',
      entityId: jobId,
      severity: 'medium',
      whyNow: '',
      nextAction: '',
      ownerRole: CATALOG_INBOX_OWNER,
      seen: true,
      seenAt: null,
    });
  };

  const handleItemClick = (item: InboxItem) => {
    if (!item.seen) {
      void markSeen(item);
    }
    if (isCatalogImportItem(item)) {
      openImportDetail(item.entityId);
      return;
    }
    openEntity(item);
  };

  const headerCountLabel =
    totalCount === 0
      ? null
      : unseenCount > 0
        ? `${unseenCount} non lu${unseenCount > 1 ? 's' : ''}`
        : `${totalCount}`;

  return (
    <div className={styles.panel}>
      <div className={`${styles.shell} ${detailOpen ? styles.shellDetail : ''}`}>
        <div className={styles.viewport}>
          <div className={styles.track}>
            <div className={`${styles.pane} ${styles.listPane}`} aria-hidden={detailOpen}>
              <div className={styles.chrome}>
                <div className={styles.header}>
                  <span>Boîte de réception</span>
                  {headerCountLabel ? (
                    <span className={styles.headerCount}>{headerCountLabel}</span>
                  ) : null}
                </div>
                <ScrollArea
                  orientation="horizontal"
                  className={styles.severityFiltersHost}
                  viewportClassName={styles.severityFilters}
                  fade="x"
                  viewportProps={{ role: 'radiogroup', 'aria-label': 'Filtrer par priorité' }}
                >
                  {SEVERITY_FILTERS.map((opt) => {
                    const active = severityFilter === opt.value;
                    const count = severityCounts[opt.value];
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={styles.severityPill}
                        style={
                          active
                            ? {
                                borderColor: opt.palette.border,
                                background: opt.palette.bg,
                                color: opt.palette.accent,
                              }
                            : undefined
                        }
                        onClick={() => setSeverityFilter(opt.value)}
                      >
                        <Icon size={14} stroke={1.75} aria-hidden />
                        <GhostWeightLabel active={active}>{opt.label}</GhostWeightLabel>
                        <span className={styles.severityPillCount}>({count})</span>
                      </button>
                    );
                  })}
                </ScrollArea>
              </div>
              <ScrollArea className={styles.listScrollHost} viewportClassName={styles.listScroll} maxHeight="none">
                {showImportCards ? (
                  <InboxBulkImportItems
                    onOpenJob={openImportDetail}
                    onDismissJob={dismissImportJob}
                    isJobSeen={isImportJobAcked}
                    onMarkJobSeen={ackImportJobFull}
                  />
                ) : null}
                <ProximityScope axis="y" className={styles.list}>
                  {filteredItems.map((i) => {
                    const unread = !i.seen;
                    const seenMeta = i.seen && i.seenAt ? formatFriendlyDate(i.seenAt) : null;
                    const action = i.nextAction?.trim() || null;
                    const showAction =
                      action &&
                      !/^(ouvrir|voir|voir détails?|ouvrir le ticket)$/i.test(action);
                    const { primary, secondary } = splitInboxWhyNow(i.whyNow);
                    const mutedLine = secondary ?? (showAction ? action : null);
                    const severityLabel =
                      i.severity === 'high' ? 'Haute' : i.severity === 'medium' ? 'Moyenne' : 'Basse';
                    return (
                      <div
                        key={i.id}
                        role="button"
                        data-wp-proximity-item=""
                        tabIndex={0}
                        data-severity={i.severity}
                        aria-label={`${i.whyNow}${action ? ` — ${action}` : ''} — ${severityLabel}${unread ? ' — non lu' : ''}`}
                        className={severityItemClass(i.severity, unread)}
                        onClick={() => handleItemClick(i)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleItemClick(i);
                          }
                        }}
                        >
                        <span className={styles.signals}>
                          <span
                            className={styles.severitySignal}
                            title={severityLabel}
                            aria-hidden
                          />
                          <EntityToneWell
                            entityType={i.entityType}
                            size="md"
                            bare
                            className={styles.entityMark}
                          />
                        </span>
                        <div className={styles.body}>
                          <span className={styles.title}>{primary}</span>
                          {mutedLine ? <span className={styles.subtitle}>{mutedLine}</span> : null}
                          <span
                            className={styles.seenLabel}
                            title={seenMeta?.detail}
                            data-visible={seenMeta ? undefined : 'empty'}
                          >
                            {seenMeta?.text ?? '\u00a0'}
                          </span>
                        </div>
                        <div className={styles.actions}>
                          <IconButton
                            type="button"
                            size="xs"
                            variant="ghost"
                            borderless
                            className={styles.dismiss}
                            aria-label="Masquer"
                            disabled={pending === i.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void dismiss(i);
                            }}
                          >
                            <IconX {...WP_ICON_NAV} />
                          </IconButton>
                        </div>
                      </div>
                    );
                  })}
                  {filteredItems.length === 0 && importVisibleCount === 0 ? (
                    <div className={styles.emptyState} role="status">
                      <EmptyState
                        density="compact"
                        title={
                          severityFilter === 'all'
                            ? 'Aucune alerte'
                            : 'Aucune alerte pour cette priorité'
                        }
                        description={
                          severityFilter === 'all'
                            ? 'Tout est à jour pour le moment.'
                            : 'Changez de filtre pour voir le reste de la boîte.'
                        }
                        illustration={
                          <StateIllustration
                            variant={severityFilter === 'all' ? 'empty' : 'search'}
                            size="sm"
                            tone="soft"
                          />
                        }
                      />
                    </div>
                  ) : null}
                </ProximityScope>
              </ScrollArea>
            </div>
            <div className={`${styles.pane} ${styles.detailPane}`}>
              {selectedJobId ? (
                <Suspense
                  fallback={
                    <Text size="sm" c="dimmed">
                      Chargement…
                    </Text>
                  }
                >
                  <CatalogImportInboxDetail
                    jobId={selectedJobId}
                    onBack={() => onSelectJobId(null)}
                  />
                </Suspense>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
